import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid, Typography,
  Table, TableBody, TableRow, TableCell, TextField, Chip, Divider, Alert, CircularProgress,
  InputAdornment,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { cerrarTurno } from '../../services/cajaService';
import { generateCierreCajaPDF } from '../../services/cierreCajaPDF';
import { CLP, FINANCE_COLORS } from '../../utils/finance';

const { PRIMARY, SUCCESS, AMBER, DANGER } = FINANCE_COLORS;

const DENOMS = [50000, 20000, 10000, 5000, 2000, 1000, 500, 100];

export default function CierreCaja({ open, onClose, turno, onClosed }) {
  const { currentUser, userData } = useAuth();
  const { activeBranch } = useBranch();
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [conteo, setConteo] = useState({});
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !turno?.id) return;
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'Sales'), where('turnoId', '==', turno.id)),
      (snap) => {
        setVentas(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((v) => v.estado !== 'anulada'));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [open, turno?.id]);

  const totales = useMemo(() => {
    const t = { efectivo: 0, debito: 0, credito: 0, transferencia: 0, otros: 0, bruto: 0, neto: 0, iva: 0, count: ventas.length };
    ventas.forEach((v) => {
      (v.pagos || []).forEach((p) => {
        if (p.medio === 'efectivo') t.efectivo += p.monto || 0;
        else if (p.medio === 'debito') t.debito += p.monto || 0;
        else if (p.medio === 'credito') t.credito += p.monto || 0;
        else if (p.medio === 'transferencia') t.transferencia += p.monto || 0;
        else t.otros += p.monto || 0;
      });
      t.bruto += v.totalFinal || v.total || 0;
      t.neto += v.subtotalNeto || v.subtotal || 0;
      t.iva += v.montoIVA || v.iva || 0;
    });
    return t;
  }, [ventas]);

  // Efectivo esperado en caja = fondo apertura + ventas en efectivo
  const efectivoEsperado = (turno?.montoApertura || 0) + totales.efectivo;
  const totalContado = useMemo(
    () => DENOMS.reduce((s, d) => s + d * (Number(conteo[d]) || 0), 0),
    [conteo],
  );
  const diferencia = totalContado - efectivoEsperado;
  const hayConteo = Object.keys(conteo).length > 0 && totalContado > 0;

  const generarPDF = (payload) => {
    generateCierreCajaPDF(
      { ...turno, ...payload, cierreAt: new Date() },
      { branch: activeBranch || {} },
    );
  };

  const handleCerrar = async () => {
    if (diferencia !== 0 && !observaciones.trim()) {
      setError('Debes ingresar observaciones cuando hay diferencia de caja.');
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        usuarioCierreId: currentUser?.uid || '',
        usuarioCierreNombre: userData?.name || currentUser?.email || '',
        cuadreEfectivo: totalContado,
        diferencia,
        observacionesCierre: observaciones,
        estado: diferencia === 0 ? 'cuadrado' : 'cerrado',
        totalEfectivo: totales.efectivo, totalDebito: totales.debito, totalCredito: totales.credito,
        totalTransferencia: totales.transferencia, totalOtros: totales.otros,
        totalBruto: totales.bruto, totalNeto: totales.neto, totalIVA: totales.iva,
        conteoVentas: totales.count,
      };
      await cerrarTurno(turno.id, payload);
      generarPDF(payload);
      onClosed?.();
    } catch (e) {
      setError('Error al cerrar el turno: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const difColor = diferencia === 0 ? SUCCESS : diferencia > 0 ? AMBER : DANGER;
  const difLabel = diferencia === 0 ? 'Cuadrado' : diferencia > 0 ? `Sobrante de ${CLP(diferencia)}` : `Faltante de ${CLP(Math.abs(diferencia))}`;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: PRIMARY }}>
        Cierre de Caja · {turno?.cajaNombre}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={3}>
            {/* Resumen ventas */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Resumen del turno</Typography>
              <Table size="small">
                <TableBody>
                  {[['Efectivo', totales.efectivo], ['Débito', totales.debito], ['Crédito', totales.credito],
                    ['Transferencia', totales.transferencia], ['Otros', totales.otros]].map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ border: 0, py: 0.3 }}>{k}</TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.3 }}>{CLP(v)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, borderTop: '2px solid #E2E8F0' }}>Total ventas</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, borderTop: '2px solid #E2E8F0' }}>{CLP(totales.bruto)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Chip label={`${totales.count} transacciones`} size="small" sx={{ mt: 1 }} />
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Fondo apertura</Typography>
                <Typography variant="body2">{CLP(turno?.montoApertura || 0)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Efectivo esperado</Typography>
                <Typography variant="body2" fontWeight={700}>{CLP(efectivoEsperado)}</Typography>
              </Box>
            </Grid>

            {/* Arqueo */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Arqueo de efectivo</Typography>
              <Grid container spacing={1}>
                {DENOMS.map((d) => (
                  <Grid size={{ xs: 6 }} key={d}>
                    <TextField
                      size="small" fullWidth type="number" label={CLP(d)}
                      value={conteo[d] || ''} onChange={(e) => setConteo((p) => ({ ...p, [d]: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ endAdornment: <InputAdornment position="end">{CLP(d * (Number(conteo[d]) || 0))}</InputAdornment> }}
                    />
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Typography fontWeight={700}>Total contado</Typography>
                <Typography fontWeight={700}>{CLP(totalContado)}</Typography>
              </Box>
              {hayConteo && (
                <Chip label={difLabel} sx={{ mt: 1, bgcolor: difColor, color: '#fff', fontWeight: 700 }} />
              )}
              <TextField
                fullWidth multiline minRows={2} label="Observaciones" value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)} InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }} required={diferencia !== 0}
                helperText={diferencia !== 0 ? 'Obligatorio cuando hay diferencia' : ''}
              />
            </Grid>
          </Grid>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="error" startIcon={saving ? <CircularProgress size={16} /> : <LockIcon />}
          onClick={handleCerrar} disabled={saving || loading}>
          {saving ? 'Cerrando…' : 'Confirmar Cierre'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
